import type { ImageFilePreviewProps } from './ImageFilePreview.types';
import { IconButton } from 'Components/shared/IconButton';
import type { AxiosResponseT } from 'Interfaces/axiosResponse';
import { useUIStore } from 'Modules/ui/index.store';
import React, { useCallback, useEffect } from 'react';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { cn } from '~/lib/utils';
import { ImagePreviewModal } from './ImagePreviewModal';

const testid = 'imageFilePreview';

export const ImageFilePreview: React.FC<ImageFilePreviewProps> = ({
  description,
  externalId,
  handleDeleteFile,
  handleDownloadFile,
  isOwnMessage,
  previewUrl,
  image,
  senderName,
  messageDate,
  getFileDownloadLink,
}) => {
  const [showHoverActions, setShowHoverActions] = React.useState(false);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [isLoadingImage, setIsLoadingImage] = React.useState(false);
  const fetchInFlightRef = React.useRef(false);
  const isMountedRef = React.useRef(true);
  const { isImagePreviewOpen, setImagePreviewOpen } = useUIStore((state) => ({
    isImagePreviewOpen: state.isImagePreviewOpen,
    setImagePreviewOpen: state.setImagePreviewOpen,
  }));

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      fetchInFlightRef.current = false;
    };
  }, []);

  const fetchFullResolutionImageUrl = useCallback(async () => {
    if (fetchInFlightRef.current || imageUrl) {
      return;
    }

    fetchInFlightRef.current = true;
    if (isMountedRef.current) {
      setIsLoadingImage(true);
    }

    try {
      const fileData = (await getFileDownloadLink(
        externalId
      )) as AxiosResponseT<{ url: string; fileName: string }>;

      if (isMountedRef.current && fileData?.data?.url) {
        setImageUrl(fileData.data.url);
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Error fetching image URL:', error);
      }
    } finally {
      fetchInFlightRef.current = false;
      if (isMountedRef.current) {
        setIsLoadingImage(false);
      }
    }
  }, [externalId, getFileDownloadLink, imageUrl]);

  useEffect(() => {
    if (isImagePreviewOpen === externalId) {
      void fetchFullResolutionImageUrl();
    }
  }, [externalId, fetchFullResolutionImageUrl, isImagePreviewOpen]);

  const handleImageClick = async (
    event: React.MouseEvent<HTMLImageElement>
  ) => {
    event.stopPropagation();
    setImagePreviewOpen(externalId);
    pushToGTMDataLayer('chat_image_preview');
    await fetchFullResolutionImageUrl();
  };

  const handleCloseModal = () => {
    setImagePreviewOpen(null);
  };

  return (
    <>
      <div
        className={cn(
          'relative flex max-w-full max-h-[270px] min-h-[50px] border border-transparent',
          showHoverActions && 'border-[#e6e6e6]'
        )}
        onMouseEnter={() => setShowHoverActions(true)}
        onMouseLeave={() => setShowHoverActions(false)}
      >
        <img
          src={previewUrl}
          title={description}
          onClick={handleImageClick}
          className="max-w-full object-contain object-center cursor-pointer"
          alt={description}
        />
        {showHoverActions && (
          <div className="absolute flex bottom-0 right-0 m-[10px] gap-[5px]">
            {isOwnMessage && (
              <IconButton
                content="Delete"
                icon="delete"
                onClick={() => handleDeleteFile(externalId)}
                popupPosition="top center"
                size="small"
                testid={`${testid}-buttonDeleteFile`}
                variant="secondary"
              />
            )}
            <IconButton
              content="Download"
              icon="download"
              onClick={() => handleDownloadFile(externalId)}
              popupPosition="top center"
              size="small"
              testid={`${testid}-buttonDownloadFile`}
              variant="secondary"
            />
          </div>
        )}
      </div>

      <ImagePreviewModal
        isOpen={isImagePreviewOpen === externalId}
        onClose={handleCloseModal}
        image={image}
        imageUrl={imageUrl}
        isLoadingImage={isLoadingImage}
        senderName={senderName}
        messageDate={messageDate}
        onDownload={handleDownloadFile}
      />
    </>
  );
};
